import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { vistaDelResumen, mesDelResumen, mesAnterior } from '../../utils/resumenSeguimiento';

/**
 * Resumen mensual del seguimiento por bot (19-oct-2026). Sólo lee: el documento lo escribe el backend.
 * Vive DETRÁS de `SEGUIMIENTO_AUTO_HABILITADO` (lo monta `BotIntegrationsPanel`), y necesita la regla
 * de lectura de `seguimientoAuto_resumen` en `firestore.rules` desplegada antes de encender el flag:
 * sin ella, la lectura falla y la tarjeta dice que el resumen no está disponible, sin romper el panel.
 * Los contadores son POR POSICIÓN del toque, no por peldaño: ver `utils/resumenSeguimiento.js`.
 */
const pct = (x) => (x == null ? '—' : `${Math.round(x * 100)} %`);

export default function SeguimientoAutoResumen({ basePath, botId, timeZone }) {
  const { t } = useTranslation('bots');
  const actual = mesDelResumen(new Date(), timeZone || 'America/Santiago');
  const [mes, setMes] = useState(actual);
  const [estado, setEstado] = useState({ cargando: true, error: false, vista: null });

  useEffect(() => {
    let vivo = true;
    if (!basePath || !botId) { setEstado({ cargando: false, error: false, vista: vistaDelResumen(null) }); return undefined; }
    setEstado({ cargando: true, error: false, vista: null });
    getDoc(doc(db, `${basePath}/bots/${botId}/seguimientoAuto_resumen/${mes}`))
      .then((s) => { if (vivo) setEstado({ cargando: false, error: false, vista: vistaDelResumen(s.exists() ? s.data() : null) }); })
      .catch(() => { if (vivo) setEstado({ cargando: false, error: true, vista: null }); });
    return () => { vivo = false; };
  }, [basePath, botId, mes]);

  const meses = [actual, mesAnterior(actual), mesAnterior(mesAnterior(actual))];
  const k = (s) => `integrations.seguimientoAuto.resumen.${s}`;
  const v = estado.vista;
  return (
    <div className="integration-card seguimiento-resumen" data-testid="seguimiento-resumen">
      <h3>{t(k('titulo'))}</h3>
      <label>
        {t(k('mes'))}{' '}
        <select value={mes} onChange={(e) => setMes(e.target.value)} aria-label={t(k('mes'))}>
          {meses.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </label>
      {estado.cargando && <p>{t(k('cargando'))}</p>}
      {estado.error && <p role="status">{t(k('error'))}</p>}
      {v && v.vacio && <p role="status">{t(k('vacio'))}</p>}
      {v && !v.vacio && (
        <>
          <p className="seguimiento-resumen__nota">{t(k('porPosicion'))}</p>
          <table>
            <thead>
              <tr><th>{t(k('colToque'))}</th><th>{t(k('colEnviados'))}</th><th>{t(k('colRespondidos'))}</th><th>{t(k('colTasa'))}</th></tr>
            </thead>
            <tbody>
              {v.porToque.map((f) => (
                <tr key={f.n}><td>{t(k('toqueN'), { n: f.n })}</td><td>{f.enviados}</td><td>{f.respondidos}</td><td>{pct(f.tasa)}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="seguimiento-resumen__nota" data-testid="nota-omitidos">{t(k('notaOmitidos'))}</p>
          <p>{t(k('tasaTotal'), { tasa: pct(v.tasa) })}</p>
          <p>{t(k('sinToque'), { n: v.sinToque })}</p>
          {v.cierres.length > 0 && (
            <>
              <h4>{t(k('cierres'))}</h4>
              <ul>
                {v.cierres.map((c) => <li key={c.motivo}>{t(k(`motivo.${c.motivo}`), { defaultValue: t(k('motivo.otro')) })}: {c.n}</li>)}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}
